// SalesList.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { FiArrowRight, FiZap, FiCalendar, FiClock, FiTag } from 'react-icons/fi';
import styles from './SalesList.module.css';

const SalesList = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchActiveSales();
  }, []);

  const fetchActiveSales = async () => {
    setLoading(true);
    setError('');
    const now = new Date().toISOString();

    try {
      const { data, error } = await supabase
        .from('sales')
        .select('*')
        .eq('is_active', true)
        .lte('start_date', now)
        .gte('end_date', now)
        .order('start_date', { ascending: false });

      if (error) throw error;
      setSales(data || []);
    } catch (err) {
      console.error('Error fetching sales:', err);
      setError('Failed to load sales. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading amazing deals...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>⚠️</div>
        <h2>Oops!</h2>
        <p>{error}</p>
        <button onClick={fetchActiveSales} className={styles.retryBtn}>Retry</button>
      </div>
    );
  }

  if (sales.length === 0) {
    return (
      <div className={styles.emptyContainer}>
        <FiZap className={styles.emptyIcon} />
        <h2>No Active Sales</h2>
        <p>There are no active sales at the moment. Check back later!</p>
        <Link to="/products" className={styles.shopBtn}>Continue Shopping</Link>
      </div>
    );
  }

  return (
    <div className={styles.salesListPage}>
      {/* Hero Banner */}
      <div className={styles.heroBanner}>
        <div className={styles.heroContent}>
          <h1>🔥 Exclusive Deals</h1>
          <p>Grab these limited-time offers before they vanish!</p>
          <div className={styles.heroStats}>
            <span><FiTag /> {sales.length} Active Deals</span>
          </div>
        </div>
        <div className={styles.heroDecoration}>
          <div className={styles.floatingIcon}>🔥</div>
          <div className={styles.floatingIcon}>⚡</div>
        </div>
      </div>

      <div className={styles.container}>
        {/* Filter/Sort Bar (optional) */}
        

        {/* Sales Grid */}
        <div className={styles.salesGrid}>
          {sales.map((sale) => {
            const discountLabel = sale.discount_type === 'percentage'
              ? `${sale.discount_value}% OFF`
              : `₹${sale.discount_value} OFF`;

            const linkTo = sale.slug ? `/sales/${sale.slug}` : `/sales/${sale.id}`;

            return (
              <div key={sale.id} className={styles.saleCard}>
                <Link to={linkTo} className={styles.cardLink}>
                  <div className={styles.cardImage}>
                    <img 
                      src={sale.image_url || '/placeholder.jpg'} 
                      alt={sale.name} 
                    />
                    <div className={styles.discountBadge}>{discountLabel}</div>
                    <div className={styles.flashBadge}>⚡ Limited</div>
                  </div>
                  <div className={styles.cardContent}>
                    <h3>{sale.name}</h3>
                    {sale.description && <p>{sale.description}</p>}
                    <div className={styles.cardMeta}>
                      <span><FiCalendar /> {new Date(sale.start_date).toLocaleDateString()} - {new Date(sale.end_date).toLocaleDateString()}</span>
                    </div>
                    <div className={styles.cardFooter}>
                      <span className={styles.shopNow}>View Deal</span>
                      <FiArrowRight className={styles.arrowIcon} />
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SalesList;